import { useState, useEffect } from "react";
import { ArrowRight, Check, Loader2, Download, TrendingUp, Users, Briefcase } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LADDER = [
  { stage: "Introducing Partner", does: "Refer startups & candidates via your link", up: "Consistent, quality introductions", icon: Users },
  { stage: "Associate Partner", does: "Build a desk with mentorship (BD or 360)", up: "First placement or £50K cumulative fees", icon: TrendingUp },
  { stage: "Talent Partner", does: "Own a full desk independently", up: "Senior / Lead tiers by track record", icon: Briefcase },
];

const TRACKS = [
  { key: "business_development", title: "Business Development", focus: "Win and own client relationships", earn: "Split-desk 10 / 12 / 14% + 20% of retainers" },
  { key: "recruitment", title: "360 Recruitment", focus: "Full cycle: source & place candidates", earn: "Tier 20 / 25 / 30% + 5% Performance Bonus" },
  { key: "operations", title: "Operations", focus: "Delivery, quality & coordination", earn: "Tiered 15–30% of net revenue + 20% of retainers" },
];

const IMAGES = {
  overview: "/images/site/unsplash-1758599543152-239625.jpg",
  who: "/images/site/unsplash-1578574577315-e62677.jpg",
  mentor: "/images/site/pexels-5439438-5ec7fe.jpg",
};

const PERSONAS = [
  { title: "Agency recruiters", desc: "Ready to run a full 360 desk and earn far more than a salaried role." },
  { title: "BD & SaaS sales professionals", desc: "With founder and VC relationships who want origination upside." },
  { title: "Ex-founders & operators", desc: "With deep networks seeking uncapped, commission-based income." },
  { title: "Well-connected introducers", desc: "Who simply want to refer opportunities for a fee — no delivery." },
];

const BENEFITS = [
  "The Magnus Kor brand & credibility with founders and VCs",
  "Proven BD and recruitment playbooks + training",
  "Commission calculators, Document Studio & operational tools",
  "A dedicated mentor during your ramp",
  "Uncapped, performance-based commission (20–40% of fees)",
  "Experience-based options available to qualified candidates",
  "Your own branded ID card & partner portal",
  "Warm, inbound-ready positioning instead of cold outreach",
];

const STEPS = [
  { t: "Apply", d: "Choose your track and partner type. Takes five minutes." },
  { t: "Screening call", d: "A short conversation to confirm fit, track and network." },
  { t: "Onboarding", d: "We set up your account, ID card, portal and playbooks." },
  { t: "Ramp with a mentor", d: "Start on live mandates with hands-on guidance." },
  { t: "Graduate", d: "Become a full Talent Partner on your first placement or £50K in fees." },
];

const FAQS = [
  { q: "Is there a cost to take part in the programme?", a: "The programme includes your onboarding, brand licence, full toolkit and dedicated mentorship. Any programme investment is discussed privately with selected candidates at the offer stage, and experience-based discounts may apply." },
  { q: "Can I take part alongside my current job?", a: "Yes. Introducing Partners work flexibly with no exclusivity or fixed hours — refer opportunities in your own time and earn a referral fee on each conversion." },
  { q: "How and when do I get paid?", a: "Commission is paid after the client's payment has cleared. Introducing Partners earn a 5% referral fee; Recruiters earn 20–30% of the placement fee by tier; BD Partners earn a 10–14% split-desk share plus 20% of retainers." },
  { q: "Do I need prior recruitment experience?", a: "Not to start as an Introducing Partner — a strong network is enough. To build a desk as an Associate Partner, relevant business-development or recruiting experience helps." },
  { q: "How long until I become a full Talent Partner?", a: "It's milestone-based, not time-boxed. You graduate on your first placement or £50K in cumulative fees — whether that takes a few weeks or a few months." },
  { q: "Which track should I choose — BD, 360 Recruitment or Operations?", a: "Choose Business Development if you win clients, 360 Recruitment if you source and place candidates, and Operations if you support delivery and coordination. You select your track when you apply." },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map((f) => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
};

const AssociatePartnerProgramPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", linkedin: "", location: "",
    track: "business_development", partner_type: "introducing",
    experience: "", network: "", why: "", referred_by_id: "",
  });

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) setForm((f) => ({ ...f, referred_by_id: ref }));
    axios.get(`${API}/referral/partners`).then((r) => setPartners(r.data || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/associate-applications`, form);
      setSubmitted(true);
      toast.success("Application received — we'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="associate-program-page">
      <SEO title="Associate Partner Programme — Become a Talent Partner"
        description="Join the Magnus Kor Associate Partner Programme: build a recruitment or business-development desk under our brand. Introducing Partner → Associate Partner → Talent Partner. Uncapped commission, playbooks, tools and mentorship."
        keywords="recruitment partner programme, become a talent partner, 360 recruiter commission, business development partner, recruitment franchise alternative, associate partner programme, refer startups for a fee"
        canonical="https://magnuskor.com/associate-partner-programme"
        jsonLd={FAQ_SCHEMA} />

      {/* Hero */}
      <section className="bg-[#1A1A1A] text-white py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <span className="text-overline text-[#B8860B]">Invitation-Only</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">Associate Partner Programme</h1>
          <p className="text-lg text-gray-300 max-w-2xl">Bring your network. We provide the brand, the playbooks, the tools, the legal and invoicing engine, and a mentor. Grow your own desk — and progress on results, not time.</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <a href="#apply" className="btn-luxury bg-[#B8860B] text-white px-8 py-4" data-testid="hero-apply-btn">Apply now <ArrowRight size={16} className="inline ml-1" /></a>
            <a href={`${API}/documents/associate-program`} target="_blank" rel="noreferrer" className="btn-luxury border border-white/40 text-white px-8 py-4" data-testid="download-prospectus-btn"><Download size={15} className="inline mr-1" /> Programme prospectus</a>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 px-6 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-overline">What It Is</span>
          <h2 className="heading-section mt-3 mb-5">A partnership, not a payroll job</h2>
          <p className="text-body mb-4">The Associate Partner Programme is an invitation-only pathway for exceptional people with strong networks in the VC-backed SaaS ecosystem to build a recruitment or business-development desk under the Magnus Kor brand.</p>
          <p className="text-body">You bring the relationships. We provide the brand, the playbooks, the calculators, the legal and invoicing engine, a branded ID card, and a mentor. You keep the majority of every fee — and you progress on results, not time served.</p>
        </div>
        <img src={IMAGES.overview} alt="Two Magnus Kor talent partners shaking hands outside a modern office, representing the Associate Partner Programme" loading="lazy" width="1200" height="800" className="w-full h-[360px] object-cover rounded" data-testid="img-overview" />
      </section>

      {/* Who should apply */}
      <section className="py-20 px-6 bg-[#FAF9F6]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-overline">Who Should Apply</span>
            <h2 className="heading-section mt-3">Built for people with a network</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PERSONAS.map((p, i) => (
              <div key={p.title} className="bg-white border border-gray-200 p-6 flex gap-4" data-testid={`persona-${i}`}>
                <span className="text-2xl font-display text-[#B8860B]/30">{i + 1}</span>
                <div><h3 className="font-display text-lg mb-1">{p.title}</h3><p className="text-body text-sm">{p.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ladder */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-overline">The Partner Ladder</span>
          <h2 className="heading-section mt-3">Progress on performance, not time</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {LADDER.map((l, i) => (
            <div key={l.stage} className="border border-gray-200 p-8 relative" data-testid={`ladder-${i}`}>
              <span className="absolute top-6 right-6 text-4xl font-display text-[#B8860B]/20">{i + 1}</span>
              <l.icon size={28} className="text-[#B8860B] mb-4" strokeWidth={1.5} />
              <h3 className="font-display text-xl mb-2">{l.stage}</h3>
              <p className="text-body text-sm mb-3">{l.does}</p>
              <p className="text-xs text-gray-500"><span className="text-[#B8860B]">Move up:</span> {l.up}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tracks */}
      <section className="py-20 px-6 bg-[#FAF9F6]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-overline">Choose Your Track</span>
            <h2 className="heading-section mt-3">Three ways to earn</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TRACKS.map((t) => (
              <div key={t.key} className="bg-white border border-gray-200 p-8" data-testid={`track-${t.key}`}>
                <h3 className="font-display text-xl mb-2">{t.title}</h3>
                <p className="text-body text-sm mb-4">{t.focus}</p>
                <p className="text-sm font-medium text-[#B8860B]">{t.earn}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 text-center mt-6">100% performance-based earnings · paid after client payment clears · no caps. Programme details, including any investment and experience-based options, are shared privately with selected candidates. Operations is offered on the Associate (build-a-desk) path only.</p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-overline">Why Join</span>
          <h2 className="heading-section mt-3">What you gain as a partner</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {BENEFITS.map((b, i) => (
            <div key={i} className="flex items-start gap-3 border border-gray-200 p-5" data-testid={`benefit-${i}`}>
              <Check size={18} className="text-[#B8860B] flex-shrink-0 mt-0.5" />
              <p className="text-body text-sm">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-6 bg-[#1A1A1A] text-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <img src={IMAGES.mentor} alt="A Magnus Kor mentor coaching a new partner during a one-to-one meeting, part of the programme's ramp phase" loading="lazy" width="1200" height="650" className="w-full h-[380px] object-cover rounded order-2 lg:order-1" data-testid="img-mentor" />
          <div className="order-1 lg:order-2">
            <span className="text-overline text-[#B8860B]">How It Works</span>
            <h2 className="font-display text-3xl md:text-4xl mt-3 mb-8">Five steps to your own desk</h2>
            <div className="space-y-5">
              {STEPS.map((s, i) => (
                <div key={s.t} className="flex gap-4" data-testid={`step-${i}`}>
                  <span className="w-8 h-8 rounded-full bg-[#B8860B] text-white flex items-center justify-center text-sm flex-shrink-0">{i + 1}</span>
                  <div><h3 className="font-medium">{s.t}</h3><p className="text-sm text-gray-400">{s.d}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-overline">Questions</span>
          <h2 className="heading-section mt-3">Frequently asked</h2>
        </div>
        <div className="divide-y divide-gray-200 border-y border-gray-200">
          {FAQS.map((f, i) => (
            <details key={i} className="py-5 group" data-testid={`faq-${i}`}>
              <summary className="flex justify-between items-center cursor-pointer font-medium list-none">
                {f.q}<span className="text-[#B8860B] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="text-body text-sm mt-3">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Application */}
      <section id="apply" className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-overline">Apply</span>
            <h2 className="heading-section mt-3 mb-3">Join the programme</h2>
            <p className="text-body">Takes five minutes. We review every application personally.</p>
          </div>

          {submitted ? (
            <div className="bg-[#FAF9F6] p-12 text-center" data-testid="associate-submitted">
              <Check size={48} className="text-[#B8860B] mx-auto mb-4" />
              <h3 className="font-display text-2xl mb-2">Application received</h3>
              <p className="text-body">Thank you. We'll be in touch shortly to arrange a short screening call.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="associate-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Full Name *</label>
                  <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="input-luxury border-b border-gray-200" data-testid="assoc-name" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Email *</label>
                  <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-luxury border-b border-gray-200" data-testid="assoc-email" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Phone</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-luxury border-b border-gray-200" data-testid="assoc-phone" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">LinkedIn</label>
                  <input value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} className="input-luxury border-b border-gray-200" placeholder="linkedin.com/in/..." data-testid="assoc-linkedin" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Track *</label>
                  <select required value={form.track} onChange={(e) => setForm({ ...form, track: e.target.value })} className="input-luxury border-b border-gray-200 bg-transparent" data-testid="assoc-track">
                    <option value="business_development">Business Development</option>
                    <option value="recruitment">360 Recruitment</option>
                    <option value="operations">Operations</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Partner Type *</label>
                  <select required value={form.partner_type} onChange={(e) => setForm({ ...form, partner_type: e.target.value })} className="input-luxury border-b border-gray-200 bg-transparent" data-testid="assoc-partner-type">
                    <option value="introducing">Introducing Partner (I want to refer)</option>
                    <option value="associate">Associate Partner (I want to build a desk)</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Location</label>
                  <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input-luxury border-b border-gray-200" placeholder="City, Country" data-testid="assoc-location" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Referred by a Magnus Kor partner (optional)</label>
                  <select value={form.referred_by_id} onChange={(e) => setForm({ ...form, referred_by_id: e.target.value })} className="input-luxury border-b border-gray-200 bg-transparent" data-testid="assoc-referrer">
                    <option value="">Not referred</option>
                    {partners.map((p) => <option key={p.uid} value={p.uid}>{p.name} — {p.role_label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-2">Your relevant experience</label>
                <textarea rows={3} value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="input-luxury border border-gray-200 p-4 resize-none w-full" placeholder="Recruitment, BD, operating background..." data-testid="assoc-experience" />
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-2">Describe your network</label>
                <textarea rows={3} value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })} className="input-luxury border border-gray-200 p-4 resize-none w-full" placeholder="Which founders, VCs, or talent communities do you have access to?" data-testid="assoc-network" />
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-2">Why Magnus Kor?</label>
                <textarea rows={3} value={form.why} onChange={(e) => setForm({ ...form, why: e.target.value })} className="input-luxury border border-gray-200 p-4 resize-none w-full" data-testid="assoc-why" />
              </div>
              <button type="submit" disabled={submitting} className="btn-luxury btn-luxury-dark w-full" data-testid="assoc-submit">
                {submitting ? <Loader2 size={18} className="animate-spin" /> : "Submit Application"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default AssociatePartnerProgramPage;
